import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useRef, useEffect, useCallback, } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import ProductCard from './product/ProductCard';
import WebPageCard from './WebPageCard';
import PortalItemCard from './PortalItemCard';
import { DEFAULT_CAROUSEL_CARD_WIDTH } from './constants/dualModeConstants';
import { useConversationStore } from '../ConversationStoreProvider';
const InChatCarousel = ({ carouselItems, cardConfig, openWebPagesInNewTab, onProductClick, onPortalRelativeNavigate, carouselCardWidth: carouselCardWidthProp, maxCarouselItems = 5, }) => {
    const store = useConversationStore();
    const scrollContainerRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const initialVariantId = store.customerContext?.currentPage?.product?.selectedVariantId;
    // Limit items to maxCarouselItems (default: 5)
    const limitedItems = useMemo(() => carouselItems.slice(0, maxCarouselItems), [carouselItems, maxCarouselItems]);
    // E1 (MCN-2485) — in-chat recommendation-event instrumentation.
    // Emit `recommendation_shown` once per unique set of product cards rendered
    // (an impression), and `recommendation_clicked` per product card click. These
    // ride the existing tracking beacon path (ConversationModel.sendTrackingEvent →
    // parent embed → beacon → webLogProcessor), mirroring product_click/product_view.
    const productItems = useMemo(() => limitedItems.filter((item) => item.type === 'product'), [limitedItems]);
    const shownKeyRef = useRef(null);
    useEffect(() => {
        if (productItems.length === 0)
            return;
        const productIds = productItems.map((item) => item.id || item.key);
        const shownKey = productIds.join('|');
        if (shownKeyRef.current === shownKey)
            return;
        shownKeyRef.current = shownKey;
        store.currentConversation?.sendTrackingEvent('recommendation_shown', {
            product_count: productItems.length,
            product_ids: productIds,
        });
    }, [productItems, store]);
    const handleRecommendationClick = useCallback((item) => {
        store.currentConversation?.sendTrackingEvent('recommendation_clicked', {
            product_id: item.id || item.key,
            product_name: item.title || '',
            product_url: item.url || '',
        });
    }, [store]);
    // Use prop if provided, otherwise use default constant
    // The hook may return horizontal width (450-500px) which is too large for carousel cards
    // Use a fixed width appropriate for vertical carousel cards
    const carouselCardWidth = carouselCardWidthProp || DEFAULT_CAROUSEL_CARD_WIDTH;
    // Detectar posición del scroll para mostrar/ocultar flechas
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            const threshold = 10; // Pequeño threshold para evitar problemas de precisión
            setShowLeftArrow(scrollLeft > threshold);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - threshold);
        };
        container.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial state
        // También verificar cuando cambian los items
        const resizeObserver = new ResizeObserver(() => {
            handleScroll();
        });
        resizeObserver.observe(container);
        // eslint-disable-next-line consistent-return
        return () => {
            container.removeEventListener('scroll', handleScroll);
            resizeObserver.disconnect();
        };
    }, [limitedItems]);
    // Funciones de navegación
    const scrollLeft = () => {
        const container = scrollContainerRef.current;
        if (!container || limitedItems.length === 0)
            return;
        // Obtener el ancho real de la primera card (incluyendo gap)
        const firstChild = container.firstElementChild;
        if (!firstChild)
            return;
        const firstCardWidth = firstChild.offsetWidth;
        const gap = 16; // 1rem = 16px
        const scrollAmount = firstCardWidth + gap;
        const newPosition = container.scrollLeft - scrollAmount;
        container.scrollTo({
            left: Math.max(0, newPosition),
            behavior: 'smooth',
        });
    };
    const scrollRight = () => {
        const container = scrollContainerRef.current;
        if (!container || limitedItems.length === 0)
            return;
        // Obtener el ancho real de la primera card (incluyendo gap)
        const firstChild = container.firstElementChild;
        if (!firstChild)
            return;
        const firstCardWidth = firstChild.offsetWidth;
        const gap = 16; // 1rem = 16px
        const scrollAmount = firstCardWidth + gap;
        const newPosition = container.scrollLeft + scrollAmount;
        container.scrollTo({
            left: Math.min(container.scrollWidth - container.clientWidth, newPosition),
            behavior: 'smooth',
        });
    };
    // Memoizar carouselProductFormatting por separado para evitar objetos nuevos en cada render
    const carouselProductFormatting = useMemo(() => {
        const baseFormatting = (cardConfig?.productFormatting || {});
        const result = {
            ...baseFormatting,
            orientation: 'vertical', // Carousel cards are always vertical
            useLegacyCards: false, // Ensure EnhancedProductCard is used
            // Default height 200px for carousel cards
            maxHeight: baseFormatting?.maxHeight ?? 200,
            // Default cover for carousel cards
            objectFit: baseFormatting?.objectFit ?? 'cover',
        };
        return result;
    }, [cardConfig?.productFormatting]);
    // Memoizar customStyle para evitar objetos nuevos en cada render
    const cardCustomStyle = useMemo(() => ({
        width: carouselCardWidth,
        maxWidth: carouselCardWidth,
    }), [carouselCardWidth]);
    const renderedItems = useMemo(() => limitedItems.map((item) => {
        if (item.type === 'product') {
            return (_jsx(Box, { 
                // Capture-phase click so the recommendation_clicked beacon fires for
                // any interaction inside the card (image, CTA, title) without altering
                // the card's own navigate/callback behavior. (E1 / MCN-2485)
                onClickCapture: () => handleRecommendationClick(item), sx: {
                    width: carouselCardWidth,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                }, children: _jsx(ProductCard, { title: item.title || '', image: item.image || '', description: item.description || '', url: item.url || '', price: item.price, productFormatting: carouselProductFormatting, product: item, onProductClick: onProductClick, customStyle: cardCustomStyle, initialVariantId: initialVariantId }) }, item.key));
        }
        if (item.type === 'web') {
            return (_jsx(Box, { sx: {
                    width: carouselCardWidth,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                }, children: _jsx(WebPageCard, { title: item.title, image: item.image, description: item.description, url: item.url, width: "100%", openInNewTab: openWebPagesInNewTab, config: cardConfig, onPortalRelativeNavigate: onPortalRelativeNavigate, itemType: item.type, organizationID: item.organizationID, entityId: item.id, slug: item.slug }) }, item.key));
        }
        const card = (_jsx(PortalItemCard, { config: cardConfig, title: item.title, image: item.image || item.screenshot, description: item.description, url: item.url || '', width: "100%", openInNewTab: openWebPagesInNewTab, onPortalRelativeNavigate: onPortalRelativeNavigate, product: item }, item.id));
        let itemKey;
        if (typeof item.target === 'object' && item.target?.key) {
            itemKey = item.target.key;
        }
        else if (typeof item.target === 'string') {
            itemKey = item.target;
        }
        else {
            itemKey = item.id || item.key;
        }
        return (_jsx(Box, { sx: {
                width: 'calc((100% - 2rem) / 3)',
                flexShrink: 0,
                scrollSnapAlign: 'start',
            }, children: card }, itemKey));
    }), [
        limitedItems,
        carouselProductFormatting,
        openWebPagesInNewTab,
        onProductClick,
        onPortalRelativeNavigate,
        carouselCardWidth,
        cardCustomStyle,
        cardConfig,
        initialVariantId,
        handleRecommendationClick,
    ]);
    if (!limitedItems || limitedItems.length === 0) {
        return null;
    }
    return (_jsxs(Box, { className: "mc-in-chat-carousel", sx: {
            position: 'relative',
            my: 2,
            width: '100%',
            // Max width: 5 cards * 200px + 4 gaps * 16px = 1000px + 64px = 1064px
            // But use dynamic calculation based on actual card width
            maxWidth: `calc(${carouselCardWidth} * 5 + 4rem)`,
            margin: '0 auto', // Centrar el carousel
        }, children: [_jsx(Box, { ref: scrollContainerRef, sx: {
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                    overflowX: 'scroll',
                    scrollSnapType: 'x mandatory',
                    scrollBehavior: 'smooth',
                    width: '100%', // Asegurar que el contenedor scrollable tenga ancho definido
                    // Hide scrollbar
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }, children: renderedItems }), _jsxs(Box, { sx: {
                    pointerEvents: 'none',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    alignItems: 'center',
                }, children: [showLeftArrow && (_jsx(Box, { sx: {
                            display: 'flex',
                            width: '36px', // w-9 equivalent
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            opacity: 1,
                            transition: 'opacity 0.2s',
                            paddingLeft: '8px', // 8px hacia adentro del carousel
                        }, children: _jsx(IconButton, { onClick: scrollLeft, sx: {
                                pointerEvents: 'auto',
                                position: 'relative',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'background.paper',
                                boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.05)',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                transition: 'color 0.2s ease-in-out, boxShadow 0.2s ease-in-out',
                                color: 'text.primary',
                                '&:hover': {
                                    backgroundColor: 'background.paper',
                                    color: 'primary.main',
                                    boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.1)',
                                },
                            }, children: _jsx(ChevronLeft, {}) }) })), showRightArrow && (_jsx(Box, { sx: {
                            marginLeft: 'auto',
                            display: 'flex',
                            width: '36px', // w-9 equivalent
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            opacity: 1,
                            transition: 'opacity 0.2s',
                            paddingRight: '8px', // 8px hacia adentro del carousel
                        }, children: _jsx(IconButton, { onClick: scrollRight, sx: {
                                pointerEvents: 'auto',
                                position: 'relative',
                                transform: 'translateX(50%)',
                                backgroundColor: 'background.paper',
                                boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.05)',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                transition: 'color 0.2s ease-in-out, boxShadow 0.2s ease-in-out',
                                color: 'text.primary',
                                '&:hover': {
                                    backgroundColor: 'background.paper',
                                    color: 'primary.main',
                                    boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.1)',
                                },
                            }, children: _jsx(ChevronRight, {}) }) }))] })] }));
};
InChatCarousel.defaultProps = {
    onProductClick: undefined,
    onPortalRelativeNavigate: undefined,
    carouselCardWidth: DEFAULT_CAROUSEL_CARD_WIDTH,
    maxCarouselItems: 5,
};
export default InChatCarousel;
//# sourceMappingURL=InChatCarousel.js.map